import React from 'react';
import { PricingPlan } from '../../shared/types';
import {
  formatPricingPlan,
  formatTrialPeriod,
} from '../../shared/utils/formatting';
import { CheckoutButton } from './CheckoutButton';

interface PricingTableProps {
  plans: PricingPlan[];
  customerId?: string;
  customerEmail?: string;
  successUrl?: string;
  cancelUrl?: string;
  className?: string;
  onCheckoutSuccess?: (plan: PricingPlan) => void;
  onCheckoutError?: (plan: PricingPlan, error: string) => void;
  showFeatures?: boolean;
  showTrialInfo?: boolean;
  layout?: 'grid' | 'list';
  maxColumns?: number;
}

export const PricingTable: React.FC<PricingTableProps> = ({
  plans,
  customerId,
  customerEmail,
  successUrl,
  cancelUrl,
  className = '',
  onCheckoutSuccess,
  onCheckoutError,
  showFeatures = true,
  showTrialInfo = true,
  layout = 'grid',
  maxColumns = 3,
}) => {
  if (!plans || plans.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No pricing plans available
      </div>
    );
  }

  const gridCols = Math.min(plans.length, maxColumns);
  const gridClass =
    layout === 'grid'
      ? `grid gap-6 ${gridCols === 1 ? 'grid-cols-1' : gridCols === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`
      : 'space-y-6';

  return (
    <div className={`pricing-table ${className}`}>
      <div className={gridClass}>
        {plans.map(plan => (
          <PricingCard
            key={plan.id}
            plan={plan}
            customerId={customerId}
            customerEmail={customerEmail}
            successUrl={successUrl}
            cancelUrl={cancelUrl}
            onCheckoutSuccess={() => onCheckoutSuccess?.(plan)}
            onCheckoutError={error => onCheckoutError?.(plan, error)}
            showFeatures={showFeatures}
            showTrialInfo={showTrialInfo}
          />
        ))}
      </div>
    </div>
  );
};

interface PricingCardProps {
  plan: PricingPlan;
  customerId?: string;
  customerEmail?: string;
  successUrl?: string;
  cancelUrl?: string;
  onCheckoutSuccess?: () => void;
  onCheckoutError?: (error: string) => void;
  showFeatures?: boolean;
  showTrialInfo?: boolean;
}

const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  customerId,
  customerEmail,
  successUrl,
  cancelUrl,
  onCheckoutSuccess,
  onCheckoutError,
  showFeatures = true,
  showTrialInfo = true,
}) => {
  const isPopular = plan.popular;

  return (
    <div
      className={`
      relative bg-white rounded-lg shadow-lg overflow-hidden
      ${isPopular ? 'ring-2 ring-blue-500 ring-opacity-50' : 'border border-gray-200'}
      transition-transform duration-200 hover:scale-105
    `}
    >
      {isPopular && (
        <div className="absolute top-0 left-0 right-0 bg-blue-500 text-white text-center py-2 text-sm font-medium">
          Most Popular
        </div>
      )}

      <div className={`p-6 ${isPopular ? 'pt-12' : ''}`}>
        {/* Plan Header */}
        <div className="text-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {plan.name}
          </h3>
          {plan.description && (
            <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
          )}
          <div className="mb-4">
            <span className="text-3xl font-bold text-gray-900">
              {formatPricingPlan(plan)}
            </span>
          </div>
          {showTrialInfo &&
            plan.trialPeriodDays &&
            plan.trialPeriodDays > 0 && (
              <div className="text-sm text-green-600 font-medium">
                {formatTrialPeriod(plan.trialPeriodDays)}
              </div>
            )}
        </div>

        {/* Features List */}
        {showFeatures && plan.features && plan.features.length > 0 && (
          <div className="mb-6">
            <ul className="space-y-3">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <svg
                    className="flex-shrink-0 w-5 h-5 text-green-500 mt-0.5 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700 text-sm">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA Button */}
        <div className="mt-6">
          <CheckoutButton
            priceId={plan.stripePriceId}
            customerId={customerId}
            customerEmail={customerEmail}
            successUrl={successUrl}
            cancelUrl={cancelUrl}
            trialPeriodDays={plan.trialPeriodDays}
            metadata={plan.metadata}
            onSuccess={onCheckoutSuccess}
            onError={onCheckoutError}
            className={`
              w-full justify-center py-3 px-4 text-base font-medium
              ${
                isPopular
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300'
              }
            `}
          >
            Get Started
          </CheckoutButton>
        </div>
      </div>
    </div>
  );
};

export default PricingTable;
